/**
 * Quick Capture Command Palette & Toast Notification System for SyncFlow
 */

export interface CommandItem {
  id: string;
  title: string;
  category: string;
  icon: string;
  shortcut?: string;
  action: () => void;
}

class ToastSystem {
  private container: HTMLElement;

  constructor() {
    let el = document.getElementById('toast-container');
    if (!el) {
      el = document.createElement('div');
      el.id = 'toast-container';
      el.className = 'toast-container';
      document.body.appendChild(el);
    }
    this.container = el;
  }

  show(message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info', duration = 3500) {
    const toast = document.createElement('div');
    toast.className = `toast-item toast-${type}`;

    let iconClass = 'ri-information-line';
    if (type === 'success') iconClass = 'ri-checkbox-circle-line';
    if (type === 'error') iconClass = 'ri-error-warning-line';
    if (type === 'warning') iconClass = 'ri-alert-line';

    toast.innerHTML = `
      <i class="toast-icon ${iconClass}"></i>
      <div class="toast-content">${message}</div>
      <button class="toast-close"><i class="ri-close-line"></i></button>
    `;

    const closeBtn = toast.querySelector('.toast-close');
    closeBtn?.addEventListener('click', () => {
      toast.style.opacity = '0';
      toast.style.transform = 'translateY(10px)';
      setTimeout(() => toast.remove(), 200);
    });

    this.container.appendChild(toast);

    setTimeout(() => {
      if (toast.parentElement) {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(10px)';
        setTimeout(() => toast.remove(), 200);
      }
    }, duration);
  }
}

export const toast = new ToastSystem();

export class CommandPalette {
  private backdrop: HTMLElement | null = null;
  private input: HTMLInputElement | null = null;
  private body: HTMLElement | null = null;
  private items: CommandItem[] = [];
  private filteredItems: CommandItem[] = [];
  private selectedIndex: number = 0;
  private isOpen: boolean = false;

  constructor() {
    this.createModal();
    this.bindGlobalShortcuts();
  }

  private createModal() {
    if (document.getElementById('cmd-palette-backdrop')) return;
    if (document.body.classList.contains('login-page') || document.querySelector('.login-container')) return;

    const backdrop = document.createElement('div');
    backdrop.id = 'cmd-palette-backdrop';
    backdrop.className = 'cmd-palette-backdrop';

    backdrop.innerHTML = `
      <div class="cmd-palette-modal">
        <div class="cmd-palette-header">
          <i class="ri-command-line cmd-palette-search-icon"></i>
          <input type="text" id="cmd-palette-input" class="cmd-palette-input" placeholder="Введіть команду або шукайте дії... (наприклад: 'Нова задача')" autocomplete="off">
          <span class="cmd-palette-badge">ESC для закриття</span>
        </div>
        <div class="cmd-palette-body" id="cmd-palette-body"></div>
        <div class="cmd-palette-footer">
          <span><strong>SyncFlow</strong> Quick Capture</span>
          <div class="cmd-palette-shortcuts">
            <div class="cmd-palette-shortcut"><span class="cmd-palette-kbd">↑</span><span class="cmd-palette-kbd">↓</span> Навігація</div>
            <div class="cmd-palette-shortcut"><span class="cmd-palette-kbd">↵</span> Вибрати</div>
          </div>
        </div>
      </div>
    `;

    document.body.appendChild(backdrop);

    this.backdrop = backdrop;
    this.input = backdrop.querySelector('#cmd-palette-input') as HTMLInputElement | null;
    this.body = backdrop.querySelector('#cmd-palette-body') as HTMLElement | null;

    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) this.close();
    });

    this.input?.addEventListener('input', () => this.handleInput());
    this.input?.addEventListener('keydown', (e) => this.handleKeyDown(e));
  }

  public registerCommands(commands: CommandItem[]) {
    this.items = [...this.items, ...commands];
  }

  public open() {
    if (!this.backdrop) return;
    this.isOpen = true;
    this.backdrop.classList.add('active');
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.filter('');
  }

  public close() {
    if (!this.backdrop) return;
    this.isOpen = false;
    this.backdrop.classList.remove('active');
  }

  public toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  private filter(query: string) {
    const q = query.toLowerCase().trim();
    if (!q) {
      this.filteredItems = [...this.items];
    } else {
      this.filteredItems = this.items.filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.category.toLowerCase().includes(q)
      );
    }
    this.selectedIndex = 0;
    this.render();
  }

  private handleInput() {
    if (this.input) {
      this.filter(this.input.value);
    }
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      this.close();
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (this.filteredItems.length > 0) {
        this.selectedIndex = (this.selectedIndex + 1) % this.filteredItems.length;
        this.render();
        this.scrollToSelected();
      }
      return;
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (this.filteredItems.length > 0) {
        this.selectedIndex = (this.selectedIndex - 1 + this.filteredItems.length) % this.filteredItems.length;
        this.render();
        this.scrollToSelected();
      }
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (this.filteredItems[this.selectedIndex]) {
        const item = this.filteredItems[this.selectedIndex];
        this.close();
        item.action();
      }
      return;
    }
  }

  private scrollToSelected() {
    if (!this.body) return;
    const selectedEl = this.body.querySelector('.cmd-palette-item.selected') as HTMLElement;
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'nearest' });
    }
  }

  private render() {
    if (!this.body) return;

    if (this.filteredItems.length === 0) {
      this.body.innerHTML = `
        <div style="padding: 24px; text-align: center; color: var(--text-dim);">
          Нічого не знайдено за вашим запитом.
        </div>
      `;
      return;
    }

    // Group items by category
    const categories: { [key: string]: CommandItem[] } = {};
    this.filteredItems.forEach((item) => {
      if (!categories[item.category]) categories[item.category] = [];
      categories[item.category].push(item);
    });

    let html = '';
    let globalIndex = 0;

    for (const [catName, catItems] of Object.entries(categories)) {
      html += `<div class="cmd-palette-section-title">${catName}</div>`;
      catItems.forEach((item) => {
        const isSelected = globalIndex === this.selectedIndex;
        html += `
          <div class="cmd-palette-item ${isSelected ? 'selected' : ''}" data-index="${globalIndex}">
            <i class="${item.icon} cmd-palette-item-icon"></i>
            <span class="cmd-palette-item-text">${item.title}</span>
            ${item.shortcut ? `<span class="cmd-palette-item-hint">${item.shortcut}</span>` : ''}
          </div>
        `;
        globalIndex++;
      });
    }

    this.body.innerHTML = html;

    // Attach click events
    const itemEls = this.body.querySelectorAll('.cmd-palette-item');
    itemEls.forEach((el) => {
      el.addEventListener('click', () => {
        const idx = parseInt(el.getAttribute('data-index') || '0', 10);
        if (this.filteredItems[idx]) {
          this.close();
          this.filteredItems[idx].action();
        }
      });
    });
  }

  private bindGlobalShortcuts() {
    window.addEventListener('keydown', (e) => {
      // Ctrl+K or Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        this.toggle();
      }
    });

    document.addEventListener('DOMContentLoaded', () => {
      const btn = document.getElementById('btn-quick-capture');
      if (btn) {
        btn.addEventListener('click', () => this.toggle());
      }

      const presetBtn = document.getElementById('btn-workout-preset');
      if (presetBtn) {
        presetBtn.addEventListener('click', () => {
          toast.show('Шаблон "Верх тіла (Push/Pull)" застосовано до поточного тренування!', 'success');
        });
      }
    });



    // Register standard app commands
    this.registerCommands([
      {
        id: 'new-task',
        title: 'Створити нову задачу',
        category: 'Швидкі Дії',
        icon: 'ri-checkbox-circle-line',
        shortcut: 'Дашборд / Задачі',
        action: () => {
          const btn = document.getElementById('dash-btn-add-task') || document.getElementById('btn-add-task');
          if (btn) btn.click();
          else (window as any).switchTab('tasks');
        }
      },
      {
        id: 'new-expense',
        title: 'Додати нову витрату',
        category: 'Швидкі Дії',
        icon: 'ri-wallet-3-line',
        shortcut: 'Фінанси',
        action: () => {
          const btn = document.getElementById('dash-btn-add-expense') || document.getElementById('btn-add-expense');
          if (btn) btn.click();
          else (window as any).switchTab('finances');
        }
      },
      {
        id: 'new-goal',
        title: 'Створити нову ціль',
        category: 'Швидкі Дії',
        icon: 'ri-focus-3-line',
        shortcut: 'Цілі',
        action: () => {
          const btn = document.getElementById('dash-btn-add-goal');
          if (btn) btn.click();
          else (window as any).switchTab('goals');
        }
      },
      {
        id: 'goto-dashboard',
        title: 'Перейти до Сьогодні (Дашборд)',
        category: 'Навігація',
        icon: 'ri-layout-grid-line',
        action: () => (window as any).switchTab('dashboard')
      },
      {
        id: 'goto-tasks',
        title: 'Перейти до Завдань',
        category: 'Навігація',
        icon: 'ri-checkbox-circle-line',
        action: () => (window as any).switchTab('tasks')
      },
      {
        id: 'goto-projects',
        title: 'Перейти до Проєктів',
        category: 'Навігація',
        icon: 'ri-folder-line',
        action: () => (window as any).switchTab('projects')
      },
      {
        id: 'goto-finances',
        title: 'Перейти до Фінансів',
        category: 'Навігація',
        icon: 'ri-wallet-3-line',
        action: () => (window as any).switchTab('finances')
      },
      {
        id: 'goto-training',
        title: 'Перейти до Тренувань',
        category: 'Навігація',
        icon: 'ri-run-line',
        action: () => (window as any).switchTab('training')
      },
      {
        id: 'goto-pomodoro',
        title: 'Перейти до Таймера Помодоро',
        category: 'Навігація',
        icon: 'ri-timer-line',
        action: () => (window as any).switchTab('pomodoro')
      },
      {
        id: 'goto-notes',
        title: 'Перейти до Бази Знань',
        category: 'Навігація',
        icon: 'ri-book-read-line',
        action: () => (window as any).switchTab('notes')
      },
      {
        id: 'goto-settings',
        title: 'Налаштування Системи',
        category: 'Налаштування',
        icon: 'ri-settings-4-line',
        action: () => (window as any).switchTab('settings')
      }
    ]);
  }
}

export const commandPalette = new CommandPalette();

